import React, { FC } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import { STATIC_MAP_ALL_LOCATIONS_API_PREFIX } from 'src/common/constants';
import { usePreviewModeContext } from 'src/common/providers/PreviewModeProvider';
import getStaticMapUrl from 'src/common/utils/getStaticMapUrl';
import Address from 'src/common/components/Address';
import { Locations } from 'src/common/types/api/v4/boats/search';
import styles from './LocationMap.module.scss';

const MAP_WIDTH = 709;
const MAP_HEIGHT = 309;
const ZOOM_LEVEL = 12;

export type LocationMapProps = {
  locations?: Locations;
};

const LocationMap: FC<LocationMapProps> = ({
  locations,
}) => {
  const [t, i18n] = useTranslation(['listing-page', 'admin-listing-preview']);
  const { previewMode } = usePreviewModeContext();

  if (!locations?.length) return null;
  const location = locations[0];

  const {
    street_address: streetAddress,
    street_city: streetCity,
    street_state: streetState,
    street_zip: streetZip,
    street_country: streetCountry,
    street_geos: {
      lat: streetLatitude,
      lon: streetLongitude,
    },
    departure_city: departureCity,
    departure_state: departureState,
    departure_zip: departureZip,
    departure_country: departureCountry,
    departure_geos: {
      lat: departureLatitude,
      lon: departureLongitude,
    } = {},
  } = location;

  const streetCoordinates = streetLatitude && streetLongitude
    ? `${streetLatitude},${streetLongitude}` : undefined;
  const departureCoordinates = departureLatitude && departureLongitude
    ? `${departureLatitude},${departureLongitude}` : undefined;

  const sameAsStreetAddress = streetLatitude === departureLatitude
  && streetLongitude === departureLongitude;
  const departureAddress = sameAsStreetAddress ? t('location-map--same-address') : departureCoordinates;

  const showPlaceholder = previewMode && !streetLatitude && !streetLongitude;
  const mapUrl = showPlaceholder ? undefined : getStaticMapUrl({
    departGeo: `${departureLongitude},${departureLatitude}`,
    width: MAP_WIDTH,
    height: MAP_HEIGHT,
    zoomLevel: ZOOM_LEVEL,
    lang: i18n.language,
  }, {
    prefix: STATIC_MAP_ALL_LOCATIONS_API_PREFIX,
  });

  return (
    <>
      <h3 className={styles.title}>
        {t('location-map--title')}
      </h3>
      <div className={styles.container}>
        {mapUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            alt={t('location-map--image-alt')}
            src={mapUrl}
            className={styles.mapImage}
            width={MAP_WIDTH}
            height={MAP_HEIGHT}
            loading="lazy"
          />
        )}
        {showPlaceholder && (
          <div className={styles.mapPlaceholder} />
        )}
      </div>
      {!showPlaceholder && (
        <>
          <p className={styles.description}>
            <Trans
              t={t}
              i18nKey="location-map--description"
              components={{ bold: <strong /> }}
            />
          </p>
          <div className={styles.addresses}>
            <Address
              title={t('location-map--address')}
              address={streetAddress || streetCoordinates}
              city={streetCity}
              state={streetState}
              zip={streetZip}
              country={streetCountry}
            />
            <Address
              title={t('location-map--departure')}
              iconColor="green"
              address={departureAddress}
              city={departureCity}
              state={departureState}
              zip={departureZip}
              country={departureCountry}
              sameAddress={sameAsStreetAddress}
            />
          </div>
        </>
      )}
    </>
  );
};

export default LocationMap;
